import { useCallback, useMemo } from 'react';
import { DownloadIcon, PlusIcon, TrashIcon } from 'lucide-react';
import i18n from '@/utils/i18n';

import {
  MerchantOrderStatusEnum,
  type OpsOrderFilter,
} from '@/graphql/generatedTypes';
import { useGetDeliveryAddressesForEventQuery } from '@/graphql/queries/getDeliveryAddressesForEvent.generated';
import { useCreateBufferOrderMutation } from '@/graphql/mutations/createBufferOrder.generated';
import { ApolloError } from '@apollo/client';
import { toast } from 'sonner';
import { useSendToPickingAction } from '@/views/Order/hooks/useSendToPickingAction';
import { useWarehousesQuery } from '@/graphql/queries/warehouses.generated';

import { OrderDataTable } from '@/views/Order/components/table/OrderDataTable';
import type {
  FilterDefinition,
  ToolbarAction,
} from '@/views/Order/components/table/OrderToolbar';
import type { ContainerTypeComboboxProps } from '@/views/Order/components/table/ContainerTypeDropdown';
import {
  eventOrderColumns,
  eventDefaultHiddenColumns,
} from '@/views/Order/components/table/eventColumns';
import { useOrderListState } from '@/views/Order/hooks/useOrderListState';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Routes } from '@/utils/routes';

const PAGE_SIZE = 20;
const firstRowFilterIds = ['itemLocation', 'deliveryAddress'];

import type { EventParticipant } from './types';
import { Spinner } from '@/components/ui/spinner';

type EventOrdersTabNewProps = {
  eventId: string;
  eventName?: string | null;
  allowedContainerTypes: ContainerTypeComboboxProps['containerTypes'];
  eventParticipants: EventParticipant[];
};

export const EventOrdersTabNew = ({
  eventId,
  allowedContainerTypes,
  eventParticipants,
}: EventOrdersTabNewProps) => {
  const baseFilter = useMemo<OpsOrderFilter>(
    () => ({ system: 'EVENT', event_id: eventId }),
    [eventId],
  );

  // Drop-off location options from delivery addresses query
  const { data: deliveryAddressData } = useGetDeliveryAddressesForEventQuery({
    variables: { event_id: eventId },
  });

  const list = useOrderListState({
    baseFilter,
    systemType: 'EVENT',
    defaultColumnVisibility: eventDefaultHiddenColumns,
    pageSize: PAGE_SIZE,
  });

  const [createBufferOrder, { loading: creatingBuffer }] =
    useCreateBufferOrderMutation();

  const handleCreateBufferOrder = useCallback(async () => {
    try {
      await createBufferOrder({
        variables: { event_id: eventId },
      });
      toast.success(i18n.t('Order:toastBufferCreated'));
      list.setActiveFilters((prev) =>
        prev.map((f) => ({ ...f, value: '__all__' })),
      );
      list.setCurrentPage(1);
      list.refetch();
    } catch (err) {
      let reason = i18n.t('Order:toastBufferCreateError');
      if (err instanceof ApolloError && err.graphQLErrors.length > 0) {
        const ext = err.graphQLErrors[0].extensions;
        reason =
          (ext?.exception as Record<string, string> | undefined)?.reason ??
          (ext?.reason as string | undefined) ??
          err.graphQLErrors[0].message ??
          reason;
      }
      toast.error(i18n.t('Order:toastBufferCreateError'), {
        description: reason,
      });
    }
  }, [createBufferOrder, eventId, list]);

  // Stable filter options derived from event-level data (don't change on page)
  const eventFilterOptions = useMemo(() => {
    const eventAccountOptions = eventParticipants.map((p) => {
      const name = p.name ?? p.id;
      return { value: p.id, getLabel: () => name };
    });

    const itemLocationSet = new Map<string, string>();
    for (const participant of eventParticipants) {
      for (const loc of participant.item_locations ?? []) {
        itemLocationSet.set(loc.id, loc.name);
      }
    }
    const itemLocationOptions = Array.from(
      itemLocationSet,
      ([value, name]) => ({ value, getLabel: () => name }),
    );

    const dropOffOptions = (
      deliveryAddressData?.getDeliveryAddressesForEvent ?? []
    ).map((addr) => {
      const name = addr.name ?? addr.id;
      return { getLabel: () => name, value: addr.id };
    });

    return { eventAccountOptions, itemLocationOptions, dropOffOptions };
  }, [eventParticipants, deliveryAddressData]);

  const { data: warehousesData } = useWarehousesQuery();

  const warehouseOptions = useMemo(
    () =>
      (warehousesData?.warehouses ?? []).map((w) => ({
        value: w.id,
        getLabel: () => w.name ?? w.text_id,
      })),
    [warehousesData],
  );

  const availableFilters = useMemo(() => {
    return list.availableFilters.map((def): FilterDefinition => {
      switch (def.id) {
        case 'eventAccount':
          return { ...def, options: eventFilterOptions.eventAccountOptions };
        case 'itemLocation':
          return { ...def, options: eventFilterOptions.itemLocationOptions };
        case 'deliveryAddress':
          return { ...def, options: eventFilterOptions.dropOffOptions };
        case 'warehouse':
          return { ...def, options: warehouseOptions };
        default:
          return def;
      }
    });
  }, [eventFilterOptions, warehouseOptions, list.availableFilters]);

  const getRowHref = useCallback(
    (order: { id: string }) => Routes.eventOrderDetail(eventId, order.id),
    [eventId],
  );

  const { handleSendToPicking } = useSendToPickingAction({
    filter: list.filter,
    onSuccess: () => {
      toast.success(i18n.t('Order:toastSendToPickingSuccess'));
      list.refetch();
    },
    onPartialError: () => {
      list.setActiveFilters([
        { id: 'status', value: MerchantOrderStatusEnum.Error },
      ]);
    },
  });

  const primaryAction = useMemo<ToolbarAction>(
    () => ({
      label: i18n.t('Order:sendToPicking'),
      requiredStatus: MerchantOrderStatusEnum.SalesOrderSent,
      onClick: handleSendToPicking,
    }),
    [handleSendToPicking],
  );

  const overflowActions = useMemo<ToolbarAction[]>(
    () => [
      {
        label: i18n.t('Order:exportCsv'),
        icon: <DownloadIcon />,
        onClick: list.handleExport,
      },
      {
        label: i18n.t('Order:deleteOrders'),
        icon: <TrashIcon />,
        onClick: list.handleCancel,
        variant: 'destructive',
      },
    ],
    [list.handleExport, list.handleCancel],
  );

  return (
    <OrderDataTable
      header={
        <>
          <div className="tw:flex tw:items-start tw:justify-between tw:gap-4">
            <div className="tw:flex tw:flex-col tw:gap-2">
              <h1 className="tw:text-4xl tw:font-semibold">
                {i18n.t('Event:eventOrders')}
              </h1>
              <p className="tw:leading-6">
                {i18n.t('Order:eventOrdersListDescription')}
              </p>
            </div>
            <Button onClick={handleCreateBufferOrder} disabled={creatingBuffer}>
              {creatingBuffer ? <Spinner /> : <PlusIcon />}
              {i18n.t('Order:addBufferOrder')}
            </Button>
          </div>

          <Separator />
        </>
      }
      columns={eventOrderColumns}
      columnVisibility={list.columnVisibility}
      onColumnVisibilityChange={list.setColumnVisibility}
      data={list.orders}
      totalCount={list.totalCount}
      currentPage={list.currentPage}
      pageSize={PAGE_SIZE}
      onPageChange={list.setCurrentPage}
      isLoading={list.loading}
      isError={list.error != null}
      sorting={list.sorting}
      onSortingChange={list.setSorting}
      containerTypes={allowedContainerTypes}
      selectedContainerTypes={list.selectedContainerTypes}
      onContainerTypeChange={list.handleContainerTypeChange}
      availableFilters={availableFilters}
      activeFilters={list.activeFilters}
      onFilterChange={list.handleFilterChange}
      onFilterAdd={list.handleFilterAdd}
      onFilterRemove={list.handleFilterRemove}
      getRowHref={getRowHref}
      primaryAction={primaryAction}
      overflowActions={overflowActions}
      firstRowFilterIds={firstRowFilterIds}
      onCancel={list.handleCancel}
      onUpdateStatus={list.handleUpdateStatus}
    />
  );
};
